import {createSelector} from '@reduxjs/toolkit'

import {isEqual} from '@shared/helpers/lang'
import {omit} from '@shared/helpers/object'

import {checkPersonalCustomsCode} from '@web/helpers/policies'

import {DEFAULT_DELIVERY_MANAGER_TYPE, DELIVERY_MANAGER_TYPE} from './constants'
import {RootState} from './interfaces'

export const deliveryManagerSelector = (state: RootState) => state.deliveryManager
export const deliveryAddressListSelector = (state: RootState) =>
    state.deliveryManager.deliveryAddressBook.deliveryAddressList
export const deliveryAdderssSelector = (state: RootState) => state.deliveryManager.deliveryAddress
export const deliveryManagerSceneSelector = (state: RootState) => state.deliveryManager.status.scene
export const deliveryManagerTypeSelector = (state: RootState) => state.deliveryManager.options.type
export const orderInfoSelector = (state: RootState) => state.deliveryManager.orderInfo

/** 통관고유부호 관련 selector */
export const personalCustomsCodeManagerSelector = createSelector(
    (state: RootState) => state.deliveryManager.deliveryAddress,
    (state: RootState) => state.deliveryManager.orderInfo.personalCustomsCode,
    (currentDelivery, personalCustomsCode) => {
        const {receiverName, telNo1} = currentDelivery ?? {}
        return {
            needPersonalCustomsCode: !!personalCustomsCode,
            personalCustomsCode: personalCustomsCode?.maskedCode ?? '',
            personalCustomsCodeHash: personalCustomsCode?.hash ?? '',
            personalCustomsCodeError: personalCustomsCode?.errorStatus ?? 'IDLE',
            receiverName: receiverName ?? '',
            telNo1: telNo1 ?? '',
            selectedPersonalCustomsCode: personalCustomsCode?.newCode ?? '',
            isValid: !personalCustomsCode || checkPersonalCustomsCode(personalCustomsCode?.newCode ?? ''),
        }
    },
)

/** 웹스토리지에 저장할 스토어 필드들을 만드는 셀렉터 */
export const saveStoragePayloadSelector = createSelector(
    deliveryManagerSelector,
    ({addressSearch, address, deliveryAddress, orderInfo, options: {type}}) => {
        return {
            type: type || DEFAULT_DELIVERY_MANAGER_TYPE,
            address,
            deliveryAddress,
            orderInfo,
            searchQuery: addressSearch.query,
        }
    },
)

export const deliveryManagerCaseSelector = createSelector(deliveryManagerSelector, ({options: {type}}) => {
    const ordersheetCase = [
        DELIVERY_MANAGER_TYPE.SELECT_DELIVERY_ADDRESS_NORMAL,
        DELIVERY_MANAGER_TYPE.SELECT_DELIVERY_ADDRESS_NO_HASH,
        DELIVERY_MANAGER_TYPE.ADD_FIRST_ADDRESS,
        DELIVERY_MANAGER_TYPE.CORRECT_INVALID_DELIVERY_ADDRESS,
    ]
    const resultCase = [
        DELIVERY_MANAGER_TYPE.CHANGE_CONTACT_ONLY,
        DELIVERY_MANAGER_TYPE.SELECT_DELIVERY_INFO_NORMAL,
        DELIVERY_MANAGER_TYPE.SHOW_MEMO_FIRST,
    ]
    const zipcodeCase = [DELIVERY_MANAGER_TYPE.SELECT_ADDRESS_NORMAL]

    const initializedType = type || ''

    const isOrdersheetCase = ordersheetCase.includes(initializedType)
    const isResultCase = resultCase.includes(initializedType)
    const isZipcodeCase = zipcodeCase.includes(initializedType)

    return {
        isOrdersheetCase,
        isResultCase,
        isZipcodeCase,
    }
})

const CustomBadge = '직접입력'

/**
 * 배송지명 판단.
 * 뱃지가 선택되어 있으면 뱃지값을 배송지명으로 결정한다.
 * */
export const deliveryAddressNameSelector = createSelector(
    deliveryManagerSelector,
    ({deliveryAddress, status: {scene}}) => {
        const useBadgeValue = scene === 'FORM'
        if (!useBadgeValue) {
            return deliveryAddress?.addressName ?? ''
        }

        const addressName = deliveryAddress?.addressName ?? ''
        const badgeValue = deliveryAddress?.addressNameBadge

        // 뱃지가 미선택되거나 '직접입력뱃지+빈배송지명' 인 경우 수신자명으로 대체
        const fallbackAddressName = deliveryAddress?.receiverName ?? ''

        if (badgeValue) {
            if (badgeValue === CustomBadge) {
                return addressName || fallbackAddressName
            } else {
                return badgeValue
            }
        }

        return fallbackAddressName
    },
)

/** 주문서 addressInfo slice에 전달할 선택된주소 반환 셀렉터 */
export const baseAddressInfoSelector = createSelector(
    deliveryManagerSelector,
    deliveryAddressNameSelector,
    ({deliveryAddress}, addressName) => {
        const deliveryAddressPayload = {
            ...omit(['addressNameBadge'], deliveryAddress),
            addressName,
        }

        return deliveryAddressPayload
    },
)

/** 해시값으로 통합배송지리스트에서 일치하는 배송지 찾아서 반환 */
export const deliveryAddressByHashSelector = createSelector(deliveryManagerSelector, ({deliveryAddressBook}) => {
    const {deliveryAddressList, selectedHash} = deliveryAddressBook
    const selectedAddress = deliveryAddressList.find(({hash, isDeleted}) => hash === selectedHash && !isDeleted)

    return selectedAddress ?? null
})

/** 배송지수정화면의 폼 데이터를 반환하는 셀렉터 */
export const deliveryAddressFormSelector = createSelector(deliveryManagerSelector, ({deliveryAddress, orderInfo}) => {
    return deliveryAddress
        ? {
              addressName: deliveryAddress?.addressName ?? '',
              receiverName: deliveryAddress?.receiverName ?? '',
              zipCode: deliveryAddress?.zipCode ?? '',
              baseAddress: deliveryAddress?.baseAddress ?? '',
              detailAddress: deliveryAddress?.detailAddress ?? '',
              isRoadNameAddress: deliveryAddress?.isRoadNameAddress ?? false,
              telNo1: deliveryAddress?.telNo1 ?? '',
              telNo2: deliveryAddress?.telNo2 ?? '',
              isPrimaryAddress: deliveryAddress?.isPrimaryAddress ?? false,
              isVirtualPhoneNumberUsed: orderInfo.virtualPhoneNumber.used,
          }
        : null
})

/** 배송지 페이로드를 만들어서 반환 (통합배송지) */
export const deliveryAddressPayloadSelector = createSelector(
    deliveryManagerSelector,
    deliveryAddressNameSelector,
    ({deliveryAddress, orderInfo}, addressName) => {
        return {
            hash: deliveryAddress?.hash ?? '',
            addressName,
            receiverName: deliveryAddress?.receiverName ?? '',
            zipCode: deliveryAddress?.zipCode ?? '',
            baseAddress: deliveryAddress?.baseAddress ?? '',
            detailAddress: deliveryAddress?.detailAddress ?? '',
            isRoadNameAddress: deliveryAddress?.isRoadNameAddress ?? false,
            isRegistered: deliveryAddress?.isRegistered ?? false,
            telNo1: deliveryAddress?.telNo1 ?? '',
            telNo2: deliveryAddress?.telNo2 ?? '',
            setAsPrimaryAddress: deliveryAddress?.isPrimaryAddress ?? false,
            setAsVirtualPhoneNumberUsed: orderInfo.virtualPhoneNumber.used,
        }
    },
)

/** 배송정보 페이로드를 만들어서 반환 (주문형/정기구독) */
export const deliveryInfoPayloadSelector = createSelector(
    deliveryManagerSelector,
    baseAddressInfoSelector,
    personalCustomsCodeManagerSelector,
    (
        {
            orderInfo: {
                deliveryMemo: {
                    memoList,
                    options: {particularMemo, saveAsDefaultMemo},
                },
                isOverseasDelivery,
                virtualPhoneNumber,
            },
        },
        deliveryAddressPayload,
        {needPersonalCustomsCode, selectedPersonalCustomsCode},
    ) => {
        return {
            orderDeliveryAddress: {
                ...deliveryAddressPayload,
                detailAddress: deliveryAddressPayload.detailAddress || '',
                telNo2: deliveryAddressPayload.telNo2 || '',
                useRoadNameAddress: deliveryAddressPayload.isRoadNameAddress,
                useVirtualPhoneNumber: virtualPhoneNumber.used,
                buildingManagementNo: deliveryAddressPayload.buildingManagementNo || '',
                locationX: deliveryAddressPayload.locationX || '',
                locationY: deliveryAddressPayload.locationY || '',
            },
            uniqueMemo: particularMemo.used, // uniqueMemo true가 개별메모를 의미함.
            memos: memoList.map((memo) => ({
                productOrderNo: memo.product?.productOrderNo || '',
                memo: memo.value,
                reuseMemo: !particularMemo.used && saveAsDefaultMemo,
            })),
            overseasDelivery: isOverseasDelivery,
            ...(needPersonalCustomsCode ? {personalCustomsCode: selectedPersonalCustomsCode} : {}),
        }
    },
)

/** 배송지수정화면 기본배송지로 설정 체크박스 노출/disabled 여부 정책 */
export const primaryAddressOptionSelector = createSelector(
    deliveryAddressListSelector,
    deliveryAdderssSelector,
    deliveryManagerTypeSelector,
    (deliveryAddressList, deliveryAddress, type) => {
        // 배송지리스트 모두 등록배송지가 아닌 경우 수정시 기본배송지로 등록 강제하기 위해 비활성화.
        // 배송지리스트가 빈 경우에도 비활성화. (첫번째 배송지 등록시 기본배송지로 설정)
        const shouldForceSetPrimaryAddress = deliveryAddressList.every(({isRegistered}) => !isRegistered)

        const primiryAddressHash = deliveryAddressList.find(({isPrimaryAddress}) => isPrimaryAddress)?.hash
        const formAddressHash = deliveryAddress?.hash
        const isNewAddress = !primiryAddressHash
        // 배송지수정화면 진입시 선택한 배송지가 기본배송지인 경우 체크해제 불가하도록 비활성화. 기본배송지를 바꾸려면 다른 배송지의 수정화면에서 체크 및 등록해야함.
        const shouldPrimaryAddressUncheckLock = primiryAddressHash === formAddressHash && !isNewAddress

        return {
            show: type !== DELIVERY_MANAGER_TYPE.ADD_FIRST_ADDRESS, // todo: ui에서 사용하고 있지 않음. 제거하기.
            disabled: shouldForceSetPrimaryAddress || shouldPrimaryAddressUncheckLock,
        }
    },
)

/** 배송지수정화면에서 수정한 내용이 있는지 확인. 폼화면 진입 시 통합배송지목록에서 주소해시가 일치하는 기저장 주소와 비교 */
export const formModifiedSelector = createSelector(
    deliveryAddressListSelector,
    deliveryAdderssSelector,
    deliveryAddressNameSelector,
    (deliveryAddressList, deliveryAddress, addressNameWithBadge) => {
        const hash = deliveryAddress?.hash
        const originalSavedAddress = deliveryAddressList.find(({hash: addressHash}) => addressHash === hash)

        return !isEqual(
            {
                addressName: originalSavedAddress?.addressName,
                receiverName: originalSavedAddress?.receiverName,
                zipCode: originalSavedAddress?.zipCode,
                baseAddress: originalSavedAddress?.baseAddress,
                detailAddress: originalSavedAddress?.detailAddress,
                telNo1: originalSavedAddress?.telNo1,
                telNo2: originalSavedAddress?.telNo2,
                isPrimaryAddress: originalSavedAddress?.isPrimaryAddress,
            },
            {
                addressName: addressNameWithBadge,
                receiverName: deliveryAddress?.receiverName,
                zipCode: deliveryAddress?.zipCode,
                baseAddress: deliveryAddress?.baseAddress,
                detailAddress: deliveryAddress?.detailAddress,
                telNo1: deliveryAddress?.telNo1,
                telNo2: deliveryAddress?.telNo2,
                isPrimaryAddress: deliveryAddress?.isPrimaryAddress,
            },
        )
    },
)

/** 주소검색결과 지번주소/도로명주소 우선순위 정책 */
export const roadJibunPrioritySelector = createSelector(deliveryManagerSelector, ({address}) => {
    return {
        baseAddress: address?.roadAddress || address?.jibunAddress || '',
        zipCode: address?.roadZipcode || address?.zipcode || '',
    }
})
